import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  TextField,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useNavigate, useParams } from "react-router-dom";
import Header, { HeaderType } from "../../components/Header";
import {
  CreateVehicle,
  DeleteVehicle,
  EditVehicle,
  GetVehicle,
} from "../../services/api/vehicle-service";
import { getAccessToken, useAuth } from "../../context/auth";
import type { CreateUpdateVehicleDto } from "../../services/model/vehicle";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface VehicleProps {
  style?: React.CSSProperties;
  loading: boolean;
  isEmptyState: true;
}

type ViewVehicleWithDocDto = {
  id: string | null;
  licensePlateNumber: string | undefined;
  make: string | undefined;
  model: string | undefined;
  color: string | undefined;
  licenseDiskDoc: string | undefined;
  vin: string | undefined;
  engineNumber: string | undefined;
};

const EMPTY_VALUES = (): ViewVehicleWithDocDto => ({
  id: null,
  licensePlateNumber: "",
  make: "",
  model: "",
  color: "",
  licenseDiskDoc: "",
  vin: "",
  engineNumber: ""
});

const validationSchema = Yup.object({
  licensePlateNumber: Yup.string().required("License Plate number is required"),
  make: Yup.string().required("Make is required"),
  model: Yup.string().required("Model is required"),
  color: Yup.string().required("Color is required"),
  vin: Yup.string().required("Vin is required"),
  engineNumber: Yup.string().required("Engine Number is required"),
});

// Convert raw base64 (no prefix) → data URL for <img src>, pass through if already a data URL
const asImageDataUrl = (v?: string | null) =>
  v && v.trim()
    ? v.startsWith("data:")
      ? v
      : `data:image/jpeg;base64,${v.trim()}`
    : "";

const AddEditVehicle: React.FC<VehicleProps> = () => {
  const [canClose, setCanClose] = useState(false);
  const [vehicleFields, setVehicleFields] = useState<ViewVehicleWithDocDto>(
    EMPTY_VALUES()
  );
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false); // render Formik only when data is normalized

  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";

  const navigate = useNavigate();
  const confirm = useConfirm();
  const { tenant } = useParams<{ tenant: string }>();
  const tpath = (p: string) => (tenant ? `/${tenant}/${p}` : `/${p}`);

  // Helpers
  const isTruthyGuid = (v: string | null | undefined) =>
    !!v &&
    v !== "00000000-0000-0000-0000-000000000000" &&
    v !== "null" &&
    v !== "undefined";

  // Guard to prevent duplicate fetches in React 18 StrictMode/dev
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    if (hasLoadedRef.current) return; // already loaded once
    hasLoadedRef.current = true;
    void fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const deriveReturnTo = () => {
    // existing flag
    const accidentForm = localStorage.getItem("AccidentForm");
    if (accidentForm === "1") return "accidentsupport";

    // new generic fallback if you add later
    const stored = localStorage.getItem("vehicleReturnTo");
    if (stored === "accidentsupport" || stored === "vehiclelist") return stored;

    return "vehiclelist";
  };

  const [returnTo] = useState<"vehiclelist" | "accidentsupport">(
    deriveReturnTo()
  );

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setReady(false);

      // Read vehicle id (may be stored as raw string or JSON string)
      const raw = localStorage.getItem("viewVehicleId");
      let viewVehicleId: string | null = null;
      if (raw) {
        try {
          const j = JSON.parse(raw);
          viewVehicleId = typeof j === "string" ? j : String(j ?? "");
        } catch {
          viewVehicleId = raw;
        }
      }

      if (isTruthyGuid(viewVehicleId)) {
        // EDIT MODE
        const response = await GetVehicle(token, viewVehicleId!);
        const d = response?.data ?? {};
        setVehicleFields({
          id: d?.id ?? viewVehicleId,
          licensePlateNumber: d?.licensePlateNumber ?? "",
          make: d?.make ?? "",
          model: d?.model ?? "",
          color: d?.color ?? "",
          licenseDiskDoc: asImageDataUrl(d?.licenseDiskDoc),
          vin: d?.vin ?? "",
          engineNumber: d?.engineNumber ?? "",
        });
      } else {
        // ADD MODE
        const stored = localStorage.getItem("vehicleFormData");
        if (stored) {
          try {
            const p = JSON.parse(stored) as Partial<ViewVehicleWithDocDto>;
            setVehicleFields({
              id: p.id ?? null,
              licensePlateNumber: p.licensePlateNumber ?? "",
              make: p.make ?? "",
              model: p.model ?? "",
              color: p.color ?? "",
              licenseDiskDoc: asImageDataUrl(p.licenseDiskDoc),
              vin: p?.vin ?? "",
              engineNumber: p?.engineNumber ?? "",
            });
          } catch {
            setVehicleFields(EMPTY_VALUES());
          }
        } else {
          setVehicleFields(EMPTY_VALUES());
        }
      }
    } catch (err) {
      setVehicleFields(EMPTY_VALUES());
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) console.error(err.message);
    } finally {
      setLoading(false);
      setReady(true);
    }
  };

  const showDialog = (title: string, message: string) => {
    confirm({ catchOnCancel: false, title, description: message }).then(() => {
      if (canClose) goBack();
    });
  };

  const goBack = () => {
    localStorage.removeItem("vehicleFormData");
    localStorage.removeItem("linkedDocumentID");
    localStorage.removeItem("AccidentForm");

    navigate(tpath(returnTo), { replace: true });
  };

  // Save and go to capture page using current form values
  const goToSingleUpload = (values: ViewVehicleWithDocDto) => {
    const payload = {
      id: values.id ?? null,
      licensePlateNumber: values.licensePlateNumber ?? "",
      make: values.make ?? "",
      model: values.model ?? "",
      color: values.color ?? "",
      vin: values.vin ?? "",
      enigineNumber: values.engineNumber ?? "",
    };

    try {
      localStorage.setItem("vehicleFormData", JSON.stringify(payload));
    } catch {
      try {
        sessionStorage.setItem("vehicleFormData", JSON.stringify(payload));
      } catch {
        navigate(tpath("vehicleuploads"), { state: payload });
        return;
      }
    }

    navigate(tpath("vehicleuploads"));
  };

  const deleteRecord = () => {
    confirm({
      okOnly: false,
      catchOnCancel: false,
      title: "Confirmation",
      description: "Are you sure you would like to delete this record?",
    }).then(() => runDelete());
  };

  const runDelete = async () => {
    try {
      setLoading(true);
      const raw = window.localStorage.getItem("viewVehicleId") || "";
      let viewVehicleId: string | null = null;
      try {
        const j = JSON.parse(raw);
        viewVehicleId = typeof j === "string" ? j : String(j ?? "");
      } catch {
        viewVehicleId = raw;
      }

      if (!isTruthyGuid(viewVehicleId)) {
        setLoading(false);
        showDialog("Vehicle", "No valid record to delete.");
        return;
      }

      const response = await DeleteVehicle(token, viewVehicleId!);
      if (response) {
        setLoading(false);
        goBack();
      } else {
        setLoading(false);
        showDialog(
          "Vehicle",
          "There seems to be a problem with your internet connection. Please check your connection and try again."
        );
      }
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) console.error(err.message);
    }
  };

  return (
    <div>
      {!ready ? (
        <BrandLoader open />
      ) : (
        <Formik<ViewVehicleWithDocDto>
          key={vehicleFields.id ?? "new"}
          initialValues={vehicleFields}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            try {
              setLoading(true);

              const vehicle: CreateUpdateVehicleDto = {
                id: vehicleFields.id,
                make: values.make,
                model: values.model,
                color: values.color,
                licensePlateNumber: values.licensePlateNumber,
                vin: values.vin,
                engineNumber: values.engineNumber
              };

              let response;
              if (vehicleFields.id == null) {
                vehicle.id = "00000000-0000-0000-0000-000000000000";
                response = await CreateVehicle(vehicle, token);
              } else {
                response = await EditVehicle(vehicle, token);
              }

              setLoading(false);
              if (response) {
                showDialog("Vehicle", response.message);
              } else {
                showDialog(
                  "Vehicle",
                  "There seems to be a problem with your internet connection. Please check your connection and try again."
                );
              }
            } catch (err) {
              setLoading(false);
              showDialog(
                "Failure",
                "An exception occurred, please contact your administrator."
              );
              if (err instanceof Error) console.error(err.message);
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <>
              <Header type={HeaderType.Vehicle} />
              <Container maxWidth="sm">
                <Grid container spacing={2}>
                  <Grid size={12 as any}>
                    <Card variant="outlined">
                      <CardContent>
                        <Form noValidate>
                          <Grid container spacing={2}>
                            <Grid size={12 as any}>
                              <Button
                                onClick={() => goToSingleUpload(values)}
                                variant="contained"
                                color="secondary"
                                startIcon={<CameraAltIcon />}
                                fullWidth
                                size="large"
                              >
                                <strong>Capture License Disc</strong>
                              </Button>

                              {vehicleFields?.licenseDiskDoc && (
                                <>
                                  <Divider sx={{ pb: 3 }} />
                                  <Box
                                    sx={{ mt: 1 }}
                                    data-testid="vehicle-documents"
                                  >
                                    <Grid
                                      container
                                      justifyContent="center"
                                      alignItems="center"
                                      direction="column"
                                    >
                                      <Grid size={12 as any}>
                                        {/* Image preview container */}
                                        <Box
                                          sx={{
                                            width: "100%",
                                            maxHeight: 420, // keeps it tidy on desktop
                                            aspectRatio: "4 / 3", // consistent with capture screens
                                            bgcolor: "grey.200",
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <Box
                                            component="img"
                                            alt="doc-img"
                                            src={vehicleFields.licenseDiskDoc}
                                            sx={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "contain", // no cropping, full image in view
                                              display: "block",
                                            }}
                                          />
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                  <Divider sx={{ pb: 3 }} />
                                </>
                              )}
                            </Grid>

                            <Grid size={12 as any}>
                              <TextField
                                label="License Plate Number"
                                name="licensePlateNumber"
                                value={values.licensePlateNumber ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                fullWidth
                                variant="outlined"
                                error={Boolean(
                                  touched.licensePlateNumber &&
                                    errors.licensePlateNumber
                                )}
                                helperText={
                                  touched.licensePlateNumber &&
                                  errors.licensePlateNumber
                                }
                              />
                            </Grid>

                            <Grid size={12 as any}>
                              <TextField
                                label="Make"
                                name="make"
                                value={values.make ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                fullWidth
                                variant="outlined"
                                error={Boolean(touched.make && errors.make)}
                                helperText={touched.make && errors.make}
                              />
                            </Grid>

                            <Grid size={12 as any}>
                              <TextField
                                label="Model"
                                name="model"
                                value={values.model ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                fullWidth
                                variant="outlined"
                                error={Boolean(touched.model && errors.model)}
                                helperText={touched.model && errors.model}
                              />
                            </Grid>

                            <Grid size={12 as any}>
                              <TextField
                                label="Color"
                                name="color"
                                value={values.color ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                fullWidth
                                variant="outlined"
                                error={Boolean(touched.color && errors.color)}
                                helperText={touched.color && errors.color}
                              />
                            </Grid>

                                                        <Grid size={12 as any}>
                              <TextField
                                label="Vin"
                                name="vin"
                                value={values.vin ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                fullWidth
                                variant="outlined"
                                error={Boolean(touched.vin && errors.vin)}
                                helperText={touched.vin && errors.vin}
                              />
                            </Grid>

                            <Grid size={12 as any}>
                              <TextField
                                label="Engine Number"
                                name="engineNumber"
                                value={values.engineNumber ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                fullWidth
                                variant="outlined"
                                error={Boolean(touched.engineNumber && errors.engineNumber)}
                                helperText={touched.engineNumber && errors.engineNumber}
                              />
                            </Grid>

                            <Grid size={12 as any}>
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                onClick={() => setCanClose(true)}
                              >
                                <strong>Save</strong>
                              </Button>
                              <Divider sx={{ pb: 2 }} />
                              <Button
                                type="button"
                                value="Delete"
                                variant="contained"
                                color="secondary"
                                fullWidth
                                size="large"
                                onClick={deleteRecord}
                              >
                                <strong>Delete</strong>
                              </Button>
                            </Grid>
                          </Grid>
                        </Form>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </>
          )}
        </Formik>
      )}

      <BrandLoader open={loading} />
    </div>
  );
};

export default AddEditVehicle;
